import { Button } from "@/components/ui/button";
import { Delta } from "./delta";
import { cn } from "@/lib/utils";
import { fmtDataCurtaBR } from "@/lib/formato-mercado";
import { PRODUTOS, chaveProduto } from "@shared/mercado-produtos";
import type { HistoricoDaPracaDTO, PagouDTO, PagouProdutoDTO, ProdutoDaSemanaDTO } from "@shared/mercado-cotacoes";

/**
 * "Semana a semana" do COMPRADOR: os quatro produtos lado a lado, uma linha por
 * semana publicada, e — ligado por padrão — o que ele pagou naquela semana.
 *
 * ⚠️ Cada linha leva a PLACA DA SEMANA (ao produtor / preço de venda). Sem ela a
 * troca de base entre duas semanas leria como queda de preço — foi o bug do
 * "▼ 36,4%". A variação de cada célula já vem do servidor contra a última
 * semana da MESMA base; "—" é "não há com o que comparar", nunca zero.
 *
 * ⚠️ "Você" só é COMPARADO nas semanas ao produtor. Na semana de preço de venda
 * o número aparece esmaecido, com "· outra base", e sem diferença: aquilo é o
 * preço da caixa na praça, e a subtração não quer dizer nada.
 *
 * Nada é calculado aqui — nem a diferença. O cliente escolhe a COR pelo sinal
 * que o servidor mandou.
 */

/** "1,20" — sem "R$": na célula estreita o símbolo empurraria a variação para fora. */
function num(v: number): string {
  return v.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function ChipBase({ referencial, testId }: { referencial: string; testId?: string }) {
  const venda = referencial === "VENDA";
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2 py-px text-[11px] font-semibold whitespace-nowrap",
        venda ? "bg-info-suave text-info" : "bg-sucesso-suave text-sucesso",
      )}
      data-testid={testId}
    >
      {/* No celular a coluna tem ~70px: "preço de venda" não cabe. */}
      <span className="sm:hidden">{venda ? "venda" : "produtor"}</span>
      <span className="hidden sm:inline">{venda ? "preço de venda" : "ao produtor"}</span>
    </span>
  );
}

function Voce({ pagou, referencial }: { pagou: PagouProdutoDTO | undefined; referencial: string | null }) {
  const base = "block mt-1 text-[11px] sm:text-[11.5px] whitespace-nowrap";
  if (!pagou) return <span className={cn(base, "text-muted-foreground/70")}>você —</span>;
  if (referencial === "VENDA") {
    return (
      <span className={cn(base, "text-muted-foreground/70")} data-testid="voce-outra-base">
        você <b className="font-mono font-medium">{num(pagou.precoMedioKg)}</b>
        <span className="hidden sm:inline"> · outra base</span>
      </span>
    );
  }
  const d = pagou.diferenca;
  // Acima da praça = pagou mais que a placa (âmbar); abaixo = verde.
  const tom = d == null ? "" : d > 0.004 ? "text-atencao" : d < -0.004 ? "text-sucesso" : "text-muted-foreground";
  const texto = d == null ? "" : d > 0.004 ? `+${num(d)}` : d < -0.004 ? `−${num(Math.abs(d))}` : "=";
  return (
    <span className={cn(base, "text-muted-foreground")}>
      você <b className="font-mono font-semibold text-foreground">{num(pagou.precoMedioKg)}</b>
      {texto && <span className={cn("block sm:inline sm:ml-1 font-mono font-semibold", tom)} data-testid="voce-diferenca">{texto}</span>}
    </span>
  );
}

export function MatrizSemanal({
  historico, pagou, mostrarPagou, onMostrarPagou, onVerMais, carregandoMais, erroPagou, onTentarPagou,
}: {
  historico: HistoricoDaPracaDTO;
  pagou: PagouDTO | undefined;
  mostrarPagou: boolean;
  onMostrarPagou: (v: boolean) => void;
  onVerMais: () => void;
  carregandoMais?: boolean;
  erroPagou?: boolean;
  onTentarPagou?: () => void;
}) {
  const pagouPorSemana = new Map((pagou?.semanas ?? []).map(s => [s.semana, s]));

  return (
    <section className="rounded-lg border bg-card shadow-xs" data-testid="matriz-semanal">
      <header className="border-b px-3 sm:px-4 py-2.5 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">Semana a semana</h3>
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex rounded-full px-2 py-px text-[11px] font-semibold bg-sucesso-suave text-sucesso">ao produtor</span>
          <span className="inline-flex rounded-full px-2 py-px text-[11px] font-semibold bg-info-suave text-info">preço de venda</span>
          <button
            type="button"
            onClick={() => onMostrarPagou(!mostrarPagou)}
            aria-pressed={mostrarPagou}
            className={cn(
              "shrink-0 rounded-full border px-3 py-1.5 text-xs font-medium hover-elevate",
              mostrarPagou ? "border-primary bg-primary/10 text-primary" : "bg-card",
            )}
            data-testid="toggle-voce-pagou"
          >
            Mostrar o que você pagou
          </button>
        </div>
      </header>

      {historico.semanas.length === 0 ? (
        <div className="px-3 py-8 text-center">
          <p className="text-sm font-medium">A praça ainda não publicou nenhuma semana.</p>
          <p className="text-xs text-muted-foreground mt-1">Assim que a primeira cotação sair, ela aparece aqui.</p>
        </div>
      ) : (
        <table className="w-full table-fixed text-[13px]">
          <thead>
            <tr className="bg-muted/50 border-b text-[10.5px] uppercase tracking-wide text-muted-foreground">
              <th className="w-[24%] sm:w-[20%] text-left font-semibold px-2 sm:px-3 py-2">Semana</th>
              {PRODUTOS.map(p => (
                <th key={chaveProduto(p)} className="text-right font-semibold px-1.5 sm:px-3 py-2 whitespace-nowrap">
                  <span className="sm:hidden">{p.label.replace("Prata ", "P")}</span>
                  <span className="hidden sm:inline">{p.label}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {historico.semanas.map((s, i) => {
              const porProduto = new Map<string, ProdutoDaSemanaDTO>(s.produtos.map(p => [chaveProduto(p), p]));
              const meu = pagouPorSemana.get(s.semana);
              const meuPorProduto = new Map<string, PagouProdutoDTO>((meu?.produtos ?? []).map(p => [chaveProduto(p), p]));
              return (
                <tr
                  key={s.semana}
                  className={cn("border-b last:border-0 align-top", i === 0 && "bg-sucesso-suave/40")}
                  data-testid={`linha-semana-${s.semana}`}
                >
                  <td className="px-2 sm:px-3 py-2">
                    <b className="block font-semibold whitespace-nowrap">
                      {fmtDataCurtaBR(s.data)}
                      {i === 0 && <span className="hidden sm:inline ml-1.5 text-[11.5px] font-normal text-muted-foreground">esta semana</span>}
                    </b>
                    <span className="block mt-0.5"><ChipBase referencial={s.referencial} testId={`base-${s.semana}`} /></span>
                  </td>
                  {PRODUTOS.map(p => {
                    const c = porProduto.get(chaveProduto(p));
                    return (
                      <td key={chaveProduto(p)} className="px-1.5 sm:px-3 py-2 text-right" data-testid={`celula-${s.semana}-${chaveProduto(p)}`}>
                        {c ? (
                          <>
                            <span className={cn("block font-mono text-[13px] sm:text-sm font-semibold tabular-nums", c.referencial === "VENDA" && "text-muted-foreground")}>
                              {num(c.precoKg)}
                            </span>
                            <span className="block mt-0.5"><Delta value={c.delta} className="text-[10.5px] px-1" /></span>
                          </>
                        ) : (
                          <span className="block font-mono text-muted-foreground">—</span>
                        )}
                        {mostrarPagou && <Voce pagou={meuPorProduto.get(chaveProduto(p))} referencial={c?.referencial ?? null} />}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <footer className="border-t bg-muted/40 px-3 sm:px-4 py-2.5 text-xs text-muted-foreground flex flex-wrap items-center justify-between gap-2">
        <div className="space-y-1 max-w-[80ch]">
          <p>
            “Você” = preço médio ponderado por kg dos seus romaneios da semana (data do documento), por classificação.
            Só se compara com a praça nas semanas <b className="text-foreground/80">ao produtor</b>: preço de venda é o preço da caixa na praça, outra base.
          </p>
          {mostrarPagou && (pagou?.itensNaoMapeados ?? 0) > 0 && (
            <p data-testid="texto-nao-mapeados">
              {pagou!.itensNaoMapeados === 1
                ? "1 item de romaneio não corresponde a um produto da praça e ficou fora da média."
                : `${pagou!.itensNaoMapeados} itens de romaneio não correspondem a um produto da praça e ficaram fora da média.`}
            </p>
          )}
          {mostrarPagou && erroPagou && (
            <p className="text-erro" data-testid="erro-voce-pagou">
              Não foi possível carregar o que você pagou.{" "}
              {onTentarPagou && <button type="button" className="underline font-medium" onClick={onTentarPagou}>Tentar de novo</button>}
            </p>
          )}
        </div>
        {historico.temMais && (
          <Button variant="outline" size="sm" onClick={onVerMais} disabled={carregandoMais} data-testid="button-ver-mais-semanas">
            {carregandoMais ? "Carregando…" : "Ver mais 8 semanas"}
          </Button>
        )}
      </footer>
    </section>
  );
}
